import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Avatar,
  Rating,
  Alert,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  IconButton,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  Person as PersonIcon,
  Schedule as TimeIcon,
  DirectionsCar as CarIcon,
  ViewList as ListIcon,
  CalendarMonth as CalendarIcon,
  VerifiedUser as VerifiedIcon,
  Close as CloseIcon,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useLoading } from '../../contexts/LoadingContext';
import apiService from '../../services/apiService';
import AuthBackground from '../../components/Auth/AuthBackground';
import MessageButton from '../../components/Chat/MessageButton';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import RideDetailsModal from '../../components/RideDetailsModal';
import { useUserProfile } from '../../contexts/UserProfileContext';
import { useSocket } from '../../contexts/SocketContext';
import TranslatingText from '../../components/Common/TranslatingText';

interface Ride {
  id: string;
  driver: {
    id: string;
    firstName: string;
    lastName: string;
    preferredName?: string;
    driverRating: number;
    profilePicture?: string;
    isLicenseVerified?: boolean;
  };
  allowChat?: boolean;
  originCity: {
    id: string;
    name: string;
    province: string;
  };
  destinationCity: {
    id: string;
    name: string;
    province: string;
  };
  departureDateTime: string;
  pricePerPerson: number;
  availableSeats: number;
  totalSeats?: number;
  estimatedDurationMinutes?: number;
  vehicle?: {
    make: {
      id: string;
      name: string;
    };
    model: {
      id: string;
      name: string;
    };
    color: string;
    isDocsVerified?: boolean;
  };
  allowsLargeLuggage?: boolean;
  allowsPets?: boolean;
  allowsSmoking?: boolean;
  additionalNotes?: string;
}

interface RideRequest {
  id: string;
  passenger: {
    id: string;
    firstName: string;
    lastName: string;
    preferredName?: string;
    passengerRating?: number;
    profilePicture?: string;
  };
  originCity: {
    id: string;
    name: string;
    province: string;
  };
  destinationCity: {
    id: string;
    name: string;
    province: string;
  };
  originDetails?: string;
  destinationDetails?: string;
  preferredDateTime: string;
  timeFlexibility: number;
  passengerCount: number;
  minBudget?: number;
  maxBudget?: number;
  needsLargeLuggage?: boolean;
  needsChildSeat?: boolean;
  needsWheelchairAccess?: boolean;
  specialRequirements?: string;
  description?: string;
  status: string;
  allowChat?: boolean;
  expiresAt: string;
  bids?: Array<{
    id: string;
    priceOffer: number;
    status: string;
    driver: {
      id: string;
      firstName: string;
      lastName: string;
      preferredName?: string;
    };
  }>;
}

type SearchResult = Ride | RideRequest;

const FindRidesPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { setLoading } = useLoading();
  const { t } = useTranslation();

  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedItem, setSelectedItem] = useState<SearchResult | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [bidPrice, setBidPrice] = useState('');
  const [bidMessage, setBidMessage] = useState('');
  const [submittingBid, setSubmittingBid] = useState(false);
  // Per-request bid state for inline bidding
  const [requestBids, setRequestBids] = useState<{ [key: string]: { price: string; message: string; submitting: boolean; error: string } }>({});

  // Get search parameters from navigation state
  const searchParams = location.state || {};
  const { originCity, destinationCity, departureDate, passengers, noResults, results: initialResults, searchType } = searchParams;

  const [anyDate, setAnyDate] = useState(!departureDate || departureDate === null || departureDate === 'null');

  const isDriver = user?.role === 'DRIVER';
  const { openProfile } = useUserProfile();

  const handleProfileClick = (e: React.MouseEvent, userId: string) => {
    e.stopPropagation();
    openProfile(userId);
  };

  // Function to handle redirect to become driver
  const handleBecomeDriverRedirect = () => {
    // Get current page URL to return to
    const returnUrl = location.pathname + location.search;
    navigate('/become-driver', {
      state: { returnUrl }
    });
  };

  const { socket } = useSocket();

  useEffect(() => {
    if (initialResults && initialResults.length > 0) {
      // If results are already provided from search
      setResults(initialResults);
    } else if (originCity && destinationCity) {
      // Search for rides based on parameters
      searchRides();
    } else {
      // Load all available rides
      loadAllRides();
    }
  }, []);

  useEffect(() => {
    if (!socket) return;

    const handleUpdate = () => {
      if (originCity && destinationCity) {
        searchRides();
      } else {
        loadAllRides();
      }
    };

    socket.on('ride_created', handleUpdate);
    socket.on('ride_updated', handleUpdate);
    socket.on('ride_cancelled', handleUpdate);
    socket.on('ride_request_created', handleUpdate);
    socket.on('ride_request_updated', handleUpdate);
    socket.on('ride_request_cancelled', handleUpdate);
    socket.on('new_ride_request', handleUpdate);
    socket.on('bid_created', handleUpdate);
    socket.on('bid_updated', handleUpdate);

    return () => {
      socket.off('ride_created', handleUpdate);
      socket.off('ride_updated', handleUpdate);
      socket.off('ride_cancelled', handleUpdate);
      socket.off('ride_request_created', handleUpdate);
      socket.off('ride_request_updated', handleUpdate);
      socket.off('ride_request_cancelled', handleUpdate);
      socket.off('new_ride_request', handleUpdate);
      socket.off('bid_created', handleUpdate);
      socket.off('bid_updated', handleUpdate);
    };
  }, [socket, originCity, destinationCity]);

  useEffect(() => {
    if (selectedItem) {
      const updatedItem = results.find(r => r.id === selectedItem.id);
      if (updatedItem) {
        setSelectedItem(updatedItem);
      } else if (detailDialogOpen) {
        setDetailDialogOpen(false);
        setSelectedItem(null);
      }
    }
  }, [results]);


  // Check for rideId in query parameter to show modal
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const rideId = params.get('rideId');
    if (rideId) {
      const fetchAndShowRide = async () => {
        try {
          const response = await apiService.get(`/rides/${rideId}`);
          if (response.success && response.data.ride) {
            setSelectedItem(response.data.ride);
            setDetailDialogOpen(true);
          }
        } catch (err) {
          console.error('Failed to fetch ride for modal:', err);
        }
      };
      fetchAndShowRide();
    }
  }, [location.search]);

  const searchRides = async (specificDate?: Date) => {
    setIsLoading(true);
    setError('');
    setLoading(true, 'Searching for rides...');

    try {
      const searchParams: any = {
        originCityId: originCity?.id,
        destinationCityId: destinationCity?.id,
        passengers,
        sortBy: 'departureDateTime'
      };

      // Only add date if provided (optional) and anyDate is false
      // Check for null/undefined explicitly to handle "any date" case
      if (!anyDate && (specificDate || (departureDate && departureDate !== null && departureDate !== 'null'))) {
        const dateToUse = specificDate || new Date(departureDate);
        if (dateToUse && !isNaN(dateToUse.getTime())) {
          searchParams.departureDate = dateToUse.toISOString().split('T')[0];
        }
      }

      // Clean up search params for requests endpoint
      if (searchType === 'requested') {
        const cleanedParams: any = {};

        // Only include originCityId if it's a valid UUID
        if (searchParams.originCityId &&
          typeof searchParams.originCityId === 'string' &&
          searchParams.originCityId.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
          cleanedParams.originCityId = searchParams.originCityId;
        }

        // Only include destinationCityId if it's a valid UUID
        if (searchParams.destinationCityId &&
          typeof searchParams.destinationCityId === 'string' &&
          searchParams.destinationCityId.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
          cleanedParams.destinationCityId = searchParams.destinationCityId;
        }

        // Include other valid parameters
        if (searchParams.departureDate) {
          cleanedParams.departureDate = searchParams.departureDate;
        }
        if (searchParams.passengers && searchParams.passengers > 0) {
          cleanedParams.passengers = searchParams.passengers;
        }
        if (searchParams.sortBy) {
          cleanedParams.sortBy = searchParams.sortBy === 'departureDateTime' ? 'preferredDateTime' : searchParams.sortBy;
        }

        const endpoint = '/requests/public-search';
        const response = await apiService.get(endpoint, cleanedParams);

        if (response.success) {
          const items = response.data.requests || response.data.data?.requests || [];
          setResults(items);
        }
      } else {
        const endpoint = '/rides/search';
        const response = await apiService.get(endpoint, searchParams);

        if (response.success) {
          const items = response.data.rides || response.data.data?.rides || [];
          setResults(items);
        }
      }
    } catch (error: any) {
      console.error('Search rides error:', error);
      setError('Failed to search rides. Please try again.');
    } finally {
      setIsLoading(false);
      setLoading(false);
    }
  };

  const loadAllRides = async () => {
    setIsLoading(true);
    setError('');

    try {
      const response = await apiService.get('/rides');
      if (response.success) {
        setResults(response.data.rides || []);
      }
    } catch (error: any) {
      console.error('Load rides error:', error);
      setError('Failed to load rides. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleItemClick = (item: SearchResult) => {
    setSelectedItem(item);
    // Reset bid form when opening modal
    if (isRideRequest(item)) {
      setBidPrice('');
      setBidMessage('');
      setError('');
    }
    setDetailDialogOpen(true);
  };

  const handleBookRide = (ride: Ride) => {
    // Navigate to booking flow or show booking modal
    navigate(`/ride/${ride.id}`, { state: { ride, showBooking: true } });
  };

  const handleSubmitBid = async () => {
    if (!selectedItem || !isRideRequest(selectedItem)) return;

    const price = parseFloat(bidPrice);
    if (isNaN(price) || price <= 0) {
      setError('لطفاً یک قیمت معتبر وارد کنید');
      return;
    }

    if (selectedItem.minBudget && price < selectedItem.minBudget) {
      setError(`قیمت پیشنهادی باید حداقل $${selectedItem.minBudget} باشد`);
      return;
    }

    if (selectedItem.maxBudget && price > selectedItem.maxBudget) {
      setError(`قیمت پیشنهادی نمی‌تواند بیشتر از $${selectedItem.maxBudget} باشد`);
      return;
    }

    setSubmittingBid(true);
    setError('');

    try {
      const bidData = {
        requestId: selectedItem.id,
        priceOffer: price,
        message: bidMessage.trim() || undefined
      };

      const response = await apiService.post('/bids', bidData);

      if (response.success) {
        setBidPrice('');
        setBidMessage('');
        // Refresh the request to show the new bid
        try {
          const updatedResponse = await apiService.get(`/requests/${selectedItem.id}`);
          if (updatedResponse.success && updatedResponse.data?.request) {
            setResults(prev => prev.map(item =>
              item.id === selectedItem.id ? updatedResponse.data.request : item
            ));
            // Update selectedItem to show the new bid in the modal
            setSelectedItem(updatedResponse.data.request);
          }
        } catch (refreshError) {
          console.error('Error refreshing request:', refreshError);
          // Still show success even if refresh fails - reload the page results
          searchRides();
        }

        // Don't close the modal automatically - let user see the success
        // setDetailDialogOpen(false);
      }
    } catch (error: any) {
      console.error('Submit bid error:', error);
      setError(error.response?.data?.message || 'ارسال پیشنهاد قیمت ناموفق بود. لطفاً دوباره تلاش کنید.');
    } finally {
      setSubmittingBid(false);
    }
  };

  const isRideRequest = (item: SearchResult): item is RideRequest => {
    return 'passenger' in item && 'preferredDateTime' in item;
  };

  const isRide = (item: SearchResult): item is Ride => {
    return 'driver' in item && 'departureDateTime' in item;
  };

  const formatDateTime = (dateTime: string) => {
    const date = new Date(dateTime);
    return {
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
  };

  // Group results by date for calendar view
  const getResultsByDate = () => {
    const resultsByDate: { [key: string]: SearchResult[] } = {};
    results.forEach(item => {
      const dateTime = isRide(item) ? item.departureDateTime : item.preferredDateTime;
      const dateKey = new Date(dateTime).toISOString().split('T')[0];
      if (!resultsByDate[dateKey]) {
        resultsByDate[dateKey] = [];
      }
      resultsByDate[dateKey].push(item);
    });
    return resultsByDate;
  };

  const resultsByDate = getResultsByDate();

  // Get results for selected date in calendar
  const getFilteredResults = () => {
    if (viewMode === 'list' || !selectedDate) {
      return results;
    }
    const dateKey = selectedDate.toISOString().split('T')[0];
    return resultsByDate[dateKey] || [];
  };

  const filteredResults = getFilteredResults();

  const renderRideDetails = (ride: Ride) => {
    const { date, time } = formatDateTime(ride.departureDateTime);

    return (
      <Box>
        <Grid container spacing={3}>
          {/* Driver Info */}
          <Grid size={{ xs: 12 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Avatar
                src={ride.driver.profilePicture}
                sx={{ width: 60, height: 60, cursor: 'pointer' }}
                onClick={(e) => handleProfileClick(e, ride.driver.id)}
              >
                {(ride.driver.preferredName || ride.driver.firstName || '?')[0]}{(ride.driver.lastName || '?')[0]}
              </Avatar>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6" sx={{ fontSize: '2.8125rem' }}>
                    {ride.driver.preferredName || ride.driver.firstName} {ride.driver.lastName ? ride.driver.lastName.charAt(0) + '.' : ''}
                  </Typography>
                  {ride.driver.isLicenseVerified && (
                    <VerifiedIcon sx={{ color: '#4caf50' }} />
                  )}
                </Box>
                <Rating
                  value={ride.driver.driverRating || 0}
                  readOnly
                  precision={0.1}
                />
              </Box>
            </Box>
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* Route */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontSize: '1.96875rem' }}>
              {t('common.origin', 'Origin')}
            </Typography>
            <Typography variant="h6" gutterBottom sx={{ fontSize: '2.8125rem' }}>
              {ride.originCity.name}, {ride.originCity.province}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontSize: '1.96875rem' }}>
              {t('common.destination', 'Destination')}
            </Typography>
            <Typography variant="h6" gutterBottom sx={{ fontSize: '2.8125rem' }}>
              {ride.destinationCity.name}, {ride.destinationCity.province}
            </Typography>
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* Date & Time */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <TimeIcon />
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '1.96875rem' }}>
                {t('rideDetails.departureDateTime', 'Departure Date & Time')}
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ fontSize: '2.25rem' }}>
              {date} {t('common.at', 'at')} {time}
            </Typography>
          </Grid>

          {/* Duration */}
          {ride.estimatedDurationMinutes && (
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontSize: '1.96875rem' }}>
                {t('rideDetails.duration', 'Trip Duration')}
              </Typography>
              <Typography variant="body1" sx={{ fontSize: '2.25rem' }}>
                {Math.floor(ride.estimatedDurationMinutes / 60)} {t('common.hours', 'hours')} {t('common.and', 'and')} {ride.estimatedDurationMinutes % 60} {t('common.minutes', 'minutes')}
              </Typography>
            </Grid>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Price & Seats */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontSize: '1.96875rem' }}>
              {t('rideDetails.pricePerPerson', 'Price per Person')}
            </Typography>
            <Typography variant="h5" color="primary.main" sx={{ fontSize: '3.375rem' }}>
              ${ride.pricePerPerson} CAD
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontSize: '1.96875rem' }}>
              {t('rideDetails.availableSeats', 'Available Seats')}
            </Typography>
            <Typography variant="h6" sx={{ fontSize: '2.8125rem' }}>
              {ride.availableSeats} {t('common.of', 'of')} {ride.totalSeats || ride.availableSeats} {t('common.seats', 'seats')}
            </Typography>
          </Grid>

          {/* Vehicle Info */}
          {ride.vehicle && (
            <>
              <Divider sx={{ my: 2 }} />
              <Grid size={{ xs: 12 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <CarIcon />
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '1.96875rem' }}>
                    {t('vehicle.title', 'Vehicle')}
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ fontSize: '2.25rem' }}>
                  {t(`vehicle.colorNames.${ride.vehicle.color}`, ride.vehicle.color)} {ride.vehicle.make.name} {ride.vehicle.model.name}
                </Typography>
                {ride.vehicle.isDocsVerified && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                    <VerifiedIcon sx={{ fontSize: 16, color: '#4caf50' }} />
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1.96875rem' }}>
                      اسناد وسیله نقلیه تأیید شده است
                    </Typography>
                  </Box>
                )}
              </Grid>
            </>
          )}

          {/* Trip Conditions */}
          {(ride.allowsLargeLuggage || ride.allowsPets || ride.allowsSmoking) && (
            <>
              <Divider sx={{ my: 2 }} />
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontSize: '1.96875rem' }}>
                  {t('rideDetails.tripConditions', 'Trip Conditions')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {ride.allowsLargeLuggage && <Chip label={t('rideDetails.luggage', 'Large Luggage')} size="small" sx={{ fontSize: '1.96875rem' }} />}
                  {ride.allowsPets && <Chip label={t('rideDetails.pets', 'Pets')} size="small" sx={{ fontSize: '1.96875rem' }} />}
                  {ride.allowsSmoking && <Chip label={t('rideDetails.smokingOk', 'Smoking')} size="small" sx={{ fontSize: '1.96875rem' }} />}
                </Box>
              </Grid>
            </>
          )}

          {/* Additional Notes */}
          {ride.additionalNotes && (
            <>
              <Divider sx={{ my: 2 }} />
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontSize: '1.96875rem' }}>
                  یادداشت‌های اضافی
                </Typography>
                <TranslatingText
                  text={ride.additionalNotes}
                  variant="body2"
                  sx={{ fontSize: '1.96875rem' }}
                />
              </Grid>
            </>
          )}

          {/* Ask Question Button - Show if allowChat is true and user is not the driver */}
          {ride.allowChat !== false && user && user.id !== ride.driver.id && (
            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <MessageButton
                  userId={ride.driver.id}
                  userName={`${ride.driver.preferredName || ride.driver.firstName || ''} ${ride.driver.lastName ? ride.driver.lastName.charAt(0) + '.' : ''}`}
                  contextType="ride"
                  contextId={ride.id}
                  label="Ask Question About This Ride"
                  variant="outlined"
                  color="primary"
                  size="large"
                  sx={{
                    py: 1.5,
                    fontSize: '2.25rem',
                    fontWeight: 'bold'
                  }}
                />
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>
    );
  };

  const renderRequestDetails = (request: RideRequest) => {
    const { date, time } = formatDateTime(request.preferredDateTime);
    const hasExistingBid = isDriver && request.bids?.some(bid => bid.driver.id === user?.id);

    return (
      <Box>
        <Grid container spacing={1.5}>
          {/* Passenger Info */}
          <Grid size={{ xs: 12 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Avatar
                src={request.passenger.profilePicture}
                sx={{ width: 60, height: 60, cursor: 'pointer' }}
                onClick={(e) => handleProfileClick(e, request.passenger.id)}
              >
                {(request.passenger.preferredName || request.passenger.firstName || '?')[0]}{(request.passenger.lastName || '?')[0]}
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontSize: '4.21875rem' }}>
                  {request.passenger.preferredName || request.passenger.firstName} {request.passenger.lastName ? request.passenger.lastName.charAt(0) + '.' : ''}
                </Typography>
                {request.passenger.passengerRating && (
                  <Rating
                    value={request.passenger.passengerRating}
                    readOnly
                    precision={0.1}
                    sx={{ '& .MuiRating-icon': { fontSize: '2.25rem' } }}
                  />
                )}
              </Box>
            </Box>
          </Grid>

          <Divider sx={{ my: 1 }} />

          {/* Route */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontSize: '2.953125rem', mb: 0.5 }}>
              مبدأ
            </Typography>
            <Typography variant="h6" gutterBottom sx={{ fontSize: '4.21875rem', mb: 0.5 }}>
              {request.originCity.name}, {request.originCity.province}
            </Typography>
            {request.originDetails && (
              <TranslatingText
                text={request.originDetails}
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: '2.953125rem' }}
              />
            )}
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontSize: '2.953125rem', mb: 0.5 }}>
              مقصد
            </Typography>
            <Typography variant="h6" gutterBottom sx={{ fontSize: '4.21875rem', mb: 0.5 }}>
              {request.destinationCity.name}, {request.destinationCity.province}
            </Typography>
            {request.destinationDetails && (
              <TranslatingText
                text={request.destinationDetails}
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: '2.953125rem' }}
              />
            )}
          </Grid>

          <Divider sx={{ my: 1 }} />

          {/* Date & Time */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <TimeIcon sx={{ fontSize: '2.953125rem' }} />
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '2.953125rem' }}>
                تاریخ و زمان ترجیحی
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ fontSize: '3.375rem' }}>
              {date} ساعت {time}
            </Typography>
            {request.timeFlexibility > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: '2.953125rem' }}>
                انعطاف‌پذیری: ±{request.timeFlexibility} ساعت
              </Typography>
            )}
          </Grid>

          {/* Passengers */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontSize: '2.953125rem', mb: 0.5 }}>
              تعداد مسافران
            </Typography>
            <Typography variant="body1" sx={{ fontSize: '3.375rem' }}>
              {request.passengerCount} نفر
            </Typography>
          </Grid>

          <Divider sx={{ my: 1 }} />

          {/* Budget */}
          <Grid size={{ xs: 12 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontSize: '2.953125rem', mb: 0.5 }}>
              بودجه
            </Typography>
            {request.minBudget && request.maxBudget ? (
              <Typography variant="h6" color="secondary.main" sx={{ fontSize: '4.21875rem' }}>
                ${request.minBudget} - ${request.maxBudget} CAD
              </Typography>
            ) : request.maxBudget ? (
              <Typography variant="h6" color="secondary.main" sx={{ fontSize: '4.21875rem' }}>
                حداکثر ${request.maxBudget} CAD
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '2.953125rem' }}>
                بودجه مشخص نشده
              </Typography>
            )}
          </Grid>

          {/* Special Requirements */}
          {(request.needsLargeLuggage || request.needsChildSeat || request.needsWheelchairAccess) && (
            <>
              <Divider sx={{ my: 1 }} />
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontSize: '2.953125rem', mb: 0.5 }}>
                  نیازهای خاص
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {request.needsLargeLuggage && <Chip label="چمدان بزرگ" size="small" color="primary" sx={{ fontSize: '2.953125rem', '& .MuiChip-icon': { fontSize: '2.953125rem' } }} />}
                  {request.needsChildSeat && <Chip label="صندلی کودک" size="small" color="primary" sx={{ fontSize: '2.953125rem', '& .MuiChip-icon': { fontSize: '2.953125rem' } }} />}
                  {request.needsWheelchairAccess && <Chip label="دسترسی ویلچر" size="small" color="primary" sx={{ fontSize: '2.953125rem', '& .MuiChip-icon': { fontSize: '2.953125rem' } }} />}
                </Box>
              </Grid>
            </>
          )}

          {/* Description */}
          {request.description && (
            <>
              <Divider sx={{ my: 1 }} />
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontSize: '2.953125rem', mb: 0.5 }}>
                  توضیحات
                </Typography>
                <TranslatingText
                  text={request.description}
                  variant="body2"
                  sx={{ fontSize: '2.953125rem' }}
                />
              </Grid>
            </>
          )}

          {/* Special Requirements Text */}
          {request.specialRequirements && (
            <>
              <Divider sx={{ my: 1 }} />
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontSize: '2.953125rem', mb: 0.5 }}>
                  نیازمندی‌های ویژه
                </Typography>
                <TranslatingText
                  text={request.specialRequirements}
                  variant="body2"
                  sx={{ fontSize: '2.953125rem' }}
                />
              </Grid>
            </>
          )}

          {/* Existing Bids */}
          {request.bids && request.bids.length > 0 && (
            <>
              <Divider sx={{ my: 1 }} />
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontSize: '2.953125rem', mb: 0.5 }}>
                  پیشنهادهای موجود ({request.bids.length})
                </Typography>
                {request.bids.map((bid) => (
                  <Box key={bid.id} sx={{ mb: 0.5, p: 0.75, bgcolor: 'grey.100', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ fontSize: '2.953125rem' }}>
                      {bid.driver.preferredName || bid.driver.firstName} {bid.driver.lastName ? bid.driver.lastName.charAt(0) + '.' : ''}: ${bid.priceOffer} CAD
                    </Typography>
                  </Box>
                ))}
              </Grid>
            </>
          )}

          {/* Ask Question Button - Show if allowChat is true and user is not the passenger */}
          {request.allowChat !== false && user && user.id !== request.passenger.id && (
            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 1.5 }} />
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <MessageButton
                  userId={request.passenger.id}
                  userName={`${request.passenger.preferredName || request.passenger.firstName || ''} ${request.passenger.lastName ? request.passenger.lastName.charAt(0) + '.' : ''}`}
                  contextType="request"
                  contextId={request.id}
                  label="Ask Question About This Ride Request"
                  variant="outlined"
                  color="secondary"
                  size="large"
                  sx={{
                    py: 1.5,
                    fontSize: '2.25rem',
                    fontWeight: 'bold'
                  }}
                />
              </Box>
            </Grid>
          )}

          {/* Bid Form for Drivers or Become Driver Button */}
          {request.status === 'OPEN' && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Grid size={{ xs: 12 }}>
                {isDriver ? (
                  !hasExistingBid ? (
                    <>
                      <Typography variant="h6" gutterBottom sx={{ fontSize: '4.21875rem', mb: 0.5 }}>
                        ارسال پیشنهاد قیمت
                      </Typography>
                      {error && (
                        <Alert severity="error" sx={{ mb: 1, fontSize: '2.953125rem', '& .MuiAlert-message': { fontSize: '2.953125rem' } }}>
                          {error}
                        </Alert>
                      )}
                      <Box sx={{ mt: 1 }}>
                        <TextField
                          label="قیمت پیشنهادی (CAD)"
                          type="number"
                          fullWidth
                          value={bidPrice}
                          onChange={(e) => setBidPrice(e.target.value)}
                          sx={{ mb: 1, '& .MuiInputLabel-root': { fontSize: '2.953125rem' }, '& .MuiInputBase-input': { fontSize: '2.953125rem' }, '& .MuiFormHelperText-root': { fontSize: '2.25rem' } }}
                          InputProps={{
                            startAdornment: <MoneyIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '2.953125rem' }} />,
                          }}
                          helperText={
                            request.minBudget && request.maxBudget
                              ? `باید بین $${request.minBudget} و $${request.maxBudget} باشد`
                              : request.maxBudget
                                ? `باید کمتر از $${request.maxBudget} باشد`
                                : ''
                          }
                        />
                        <TextField
                          label="پیام (اختیاری)"
                          multiline
                          rows={3}
                          fullWidth
                          value={bidMessage}
                          onChange={(e) => setBidMessage(e.target.value)}
                          sx={{ mb: 1, '& .MuiInputLabel-root': { fontSize: '2.953125rem' }, '& .MuiInputBase-input': { fontSize: '2.953125rem' } }}
                        />
                        <Button
                          variant="contained"
                          color="secondary"
                          fullWidth
                          onClick={handleSubmitBid}
                          disabled={submittingBid || !bidPrice}
                          sx={{ fontSize: '3.375rem', py: 1 }}
                        >
                          {submittingBid ? 'در حال ارسال...' : 'ارسال پیشنهاد'}
                        </Button>
                      </Box>
                    </>
                  ) : (
                    <Alert severity="info" sx={{ fontSize: '1.96875rem', '& .MuiAlert-message': { fontSize: '1.96875rem' } }}>
                      شما قبلاً برای این درخواست پیشنهاد قیمت ارسال کرده‌اید.
                    </Alert>
                  )
                ) : user ? (
                  <Alert severity="info" sx={{ fontSize: '1.96875rem', '& .MuiAlert-message': { fontSize: '1.96875rem' } }}>
                    <Typography variant="body2" sx={{ mb: 2, fontSize: '1.96875rem' }}>
                      برای ارسال پیشنهاد قیمت باید راننده شوید.
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleBecomeDriverRedirect}
                      sx={{ fontSize: '1.96875rem' }}
                    >
                      راننده شوید
                    </Button>
                  </Alert>
                ) : (
                  <Alert severity="info" sx={{ fontSize: '1.96875rem', '& .MuiAlert-message': { fontSize: '1.96875rem' } }}>
                    <Typography variant="body2" sx={{ mb: 2, fontSize: '1.96875rem' }}>
                      لطفاً وارد شوید و راننده شوید تا بتوانید پیشنهاد قیمت ارسال کنید.
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        const returnUrl = location.pathname + location.search;
                        navigate('/login', { state: { returnUrl } });
                      }}
                      sx={{ fontSize: '1.96875rem' }}
                    >
                      ورود
                    </Button>
                  </Alert>
                )}
              </Grid>
            </>
          )}
        </Grid>
      </Box>
    );
  };

  // Custom calendar day component with badge
  const CustomDay = (props: any) => {
    const { day, ...other } = props;
    const dateKey = day.toISOString().split('T')[0];
    const dayResults = resultsByDate[dateKey] || [];
    const resultCount = dayResults.length;

    return (
      <Badge
        key={day.toString()}
        overlap="circular"
        badgeContent={resultCount > 0 ? resultCount : undefined}
        color="primary"
      >
        <PickersDay
          {...other}
          day={day}
          sx={{
            ...(resultCount > 0 && {
              fontWeight: 'bold',
              border: '2px solid',
              borderColor: 'primary.main',
            }),
          }}
        />
      </Badge>
    );
  };

  const renderResultCard = (item: SearchResult) => {
    if (isRide(item)) {
      return renderRideCard(item);
    } else {
      return renderRequestCard(item);
    }
  };

  const renderRideCard = (ride: Ride) => {
    const { date, time } = formatDateTime(ride.departureDateTime);

    return (
      <Card
        key={ride.id}
        sx={{
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: 4,
          },
          mb: 2,
          overflow: 'hidden',
          width: '100%',
          boxSizing: 'border-box'
        }}
        onClick={() => handleItemClick(ride)}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
          <Grid container spacing={2} alignItems="center">
            {/* Driver Info */}
            <Grid size={{ xs: 12, sm: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar
                  src={ride.driver.profilePicture}
                  sx={{ width: 40, height: 40, cursor: 'pointer' }}
                  onClick={(e) => handleProfileClick(e, ride.driver.id)}
                >
                  {(ride.driver.preferredName || ride.driver.firstName || '?')[0]}{(ride.driver.lastName || '?')[0]}
                </Avatar>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '1.96875rem' }}>
                      {ride.driver.preferredName || ride.driver.firstName} {ride.driver.lastName[0]}.
                    </Typography>
                    {ride.driver.isLicenseVerified && (
                      <VerifiedIcon
                        sx={{
                          fontSize: 16,
                          color: '#4caf50',
                          ml: 0.5
                        }}
                        titleAccess="Verified Driver License"
                      />
                    )}
                  </Box>
                  <Rating
                    value={ride.driver.driverRating || 0}
                    readOnly
                    size="small"
                    precision={0.1}
                  />
                </Box>
              </Box>
            </Grid>

            {/* Route */}
            <Grid size={{ xs: 12, sm: 4 }}>
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 0.5, fontSize: '2.25rem', wordBreak: 'break-word' }}>
                  {ride.originCity.name} → {ride.destinationCity.name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                  <TimeIcon fontSize="small" />
                  <Typography variant="body2">
                    {date} at {time}
                  </Typography>
                </Box>
                {ride.estimatedDurationMinutes && (
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1.96875rem' }}>
                    Duration: {Math.floor(ride.estimatedDurationMinutes / 60)}h {ride.estimatedDurationMinutes % 60}m
                  </Typography>
                )}
                {ride.vehicle && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                    <CarIcon fontSize="small" />
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1.96875rem' }}>
                      {ride.vehicle.make.name} {ride.vehicle.model.name}
                    </Typography>
                    {ride.vehicle.isDocsVerified && (
                      <VerifiedIcon
                        sx={{
                          fontSize: 14,
                          color: '#4caf50'
                        }}
                        titleAccess="Verified Vehicle Documents"
                      />
                    )}
                  </Box>
                )}
              </Box>
            </Grid>

            {/* Price and Seats */}
            <Grid size={{ xs: 12, sm: 3 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" color="primary.main" sx={{ fontWeight: 'bold', fontSize: '2.8125rem' }}>
                  ${ride.pricePerPerson}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1.96875rem' }}>
                  per person
                </Typography>
                <Chip
                  icon={<PersonIcon />}
                  label={`${ride.availableSeats} seats left`}
                  size="small"
                  color={ride.availableSeats <= 2 ? 'warning' : 'success'}
                  sx={{ mt: 1, fontSize: '1.96875rem' }}
                />
              </Box>
            </Grid>

            {/* Book Button */}
            <Grid size={{ xs: 12, sm: 2 }}>
              <Button
                variant="contained"
                fullWidth
                onClick={(e) => {
                  e.stopPropagation();
                  handleBookRide(ride);
                }}
                disabled={ride.availableSeats === 0 || ride.driver.firstName === user?.firstName}
                sx={{ fontSize: '3.375rem', py: 1.5 }}
              >
                {ride.availableSeats === 0 ? 'Full' : 'Book'}
              </Button>
            </Grid>
          </Grid>

          {/* Vehicle Info */}
          {ride.vehicle && (
            <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CarIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1.96875rem' }}>
                  {ride.vehicle.color} {ride.vehicle.make.name} {ride.vehicle.model.name}
                </Typography>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  const handleSubmitBidInline = async (request: RideRequest) => {
    const requestBid = requestBids[request.id] || { price: '', message: '', submitting: false, error: '' };
    const price = parseFloat(requestBid.price);

    if (isNaN(price) || price <= 0) {
      setRequestBids(prev => ({
        ...prev,
        [request.id]: { ...requestBid, error: 'Please enter a valid price' }
      }));
      return;
    }

    if (request.minBudget && price < request.minBudget) {
      setRequestBids(prev => ({
        ...prev,
        [request.id]: { ...requestBid, error: `Price must be at least $${request.minBudget}` }
      }));
      return;
    }

    if (request.maxBudget && price > request.maxBudget) {
      setRequestBids(prev => ({
        ...prev,
        [request.id]: { ...requestBid, error: `Price cannot exceed $${request.maxBudget}` }
      }));
      return;
    }

    setRequestBids(prev => ({
      ...prev,
      [request.id]: { ...requestBid, submitting: true, error: '' }
    }));

    try {
      const bidData = {
        requestId: request.id,
        priceOffer: price,
        message: requestBid.message.trim() || undefined
      };

      const response = await apiService.post('/bids', bidData);

      if (response.success) {
        setRequestBids(prev => ({
          ...prev,
          [request.id]: { price: '', message: '', submitting: false, error: '' }
        }));

        // Refresh the request to show the new bid
        try {
          const updatedResponse = await apiService.get(`/requests/${request.id}`);
          if (updatedResponse.success && updatedResponse.data?.request) {
            setResults(prev => prev.map(item =>
              item.id === request.id ? updatedResponse.data.request : item
            ));
          }
        } catch (refreshError) {
          console.error('Error refreshing request:', refreshError);
          // Still show success even if refresh fails - reload the page results
          searchRides();
        }
      }
    } catch (error: any) {
      console.error('Submit bid error:', error);
      setRequestBids(prev => ({
        ...prev,
        [request.id]: {
          ...requestBid,
          submitting: false,
          error: error.response?.data?.message || 'Failed to submit bid. Please try again.'
        }
      }));
    }
  };

  const renderRequestCard = (request: RideRequest) => {
    const { date, time } = formatDateTime(request.preferredDateTime);
    const hasExistingBid = isDriver && request.bids?.some(bid => bid.driver.id === user?.id);
    const requestBid = requestBids[request.id] || { price: '', message: '', submitting: false, error: '' };
    const showBidForm = isDriver && !hasExistingBid && request.status === 'OPEN';

    return (
      <Card
        key={request.id}
        onClick={() => handleItemClick(request)}
        sx={{
          transition: 'all 0.3s ease',
          cursor: 'pointer',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: 4,
          },
          mb: 2,
          border: '2px solid',
          borderColor: 'secondary.main',
          overflow: 'hidden',
          width: '100%',
          boxSizing: 'border-box'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
          <Grid container spacing={2} alignItems="center">
            {/* Passenger Info */}
            <Grid size={{ xs: 12, sm: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar
                  src={request.passenger.profilePicture}
                  sx={{ width: 40, height: 40, cursor: 'pointer' }}
                  onClick={(e) => handleProfileClick(e, request.passenger.id)}
                >
                  {(request.passenger.preferredName || request.passenger.firstName || '?')[0]}{(request.passenger.lastName || '?')[0]}
                </Avatar>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '1.96875rem' }}>
                    {request.passenger.preferredName || request.passenger.firstName} {request.passenger.lastName[0]}.
                  </Typography>
                  {request.passenger.passengerRating && (
                    <Rating
                      value={request.passenger.passengerRating}
                      readOnly
                      size="small"
                      precision={0.1}
                    />
                  )}
                </Box>
              </Box>
            </Grid>

            {/* Route */}
            <Grid size={{ xs: 12, sm: 4 }}>
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 0.5, fontSize: '2.25rem', wordBreak: 'break-word' }}>
                  {request.originCity.name} → {request.destinationCity.name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                  <TimeIcon fontSize="small" />
                  <Typography variant="body2">
                    {date} at {time}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1.96875rem' }}>
                  {request.passengerCount} passenger{request.passengerCount > 1 ? 's' : ''}
                </Typography>
              </Box>
            </Grid>

            {/* Budget */}
            <Grid size={{ xs: 12, sm: 3 }}>
              <Box sx={{ textAlign: 'center' }}>
                {request.minBudget && request.maxBudget ? (
                  <>
                    <Typography variant="h6" color="secondary.main" sx={{ fontWeight: 'bold', fontSize: '2.8125rem' }}>
                      ${request.minBudget} - ${request.maxBudget}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1.96875rem' }}>
                      Budget range
                    </Typography>
                  </>
                ) : request.maxBudget ? (
                  <>
                    <Typography variant="h6" color="secondary.main" sx={{ fontWeight: 'bold', fontSize: '2.8125rem' }}>
                      Up to ${request.maxBudget}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1.96875rem' }}>
                      Max budget
                    </Typography>
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1.96875rem' }}>
                    No budget specified
                  </Typography>
                )}
                {request.bids && request.bids.length > 0 && (
                  <Chip
                    label={`${request.bids.length} bid${request.bids.length > 1 ? 's' : ''}`}
                    size="small"
                    color="info"
                    sx={{ mt: 1, fontSize: '1.96875rem' }}
                  />
                )}
              </Box>
            </Grid>

            {/* Status/Action */}
            <Grid size={{ xs: 12, sm: 2 }}>
              {isDriver ? (
                hasExistingBid ? (
                  <Chip
                    label="Bid Sent"
                    color="success"
                    size="small"
                    sx={{ fontSize: '1.96875rem' }}
                  />
                ) : request.status !== 'OPEN' ? (
                  <Chip
                    label={request.status}
                    color="default"
                    size="small"
                    sx={{ fontSize: '1.96875rem' }}
                  />
                ) : null
              ) : (
                <Chip
                  label={request.status}
                  color={request.status === 'OPEN' ? 'success' : 'default'}
                  size="small"
                  sx={{ fontSize: '1.96875rem' }}
                />
              )}
            </Grid>

            {/* Bid Form - Show for drivers or redirect button for non-drivers */}
            {request.status === 'OPEN' && (
              <Grid size={{ xs: 12 }} onClick={(e) => e.stopPropagation()}>
                <Divider sx={{ my: 2 }} />
                {isDriver ? (
                  showBidForm ? (
                    <Box>
                      {requestBid.error && (
                        <Alert severity="error" sx={{ mb: 2, fontSize: '1.5em', '& .MuiAlert-message': { fontSize: '1.5em' } }}>
                          {requestBid.error}
                        </Alert>
                      )}
                      <Grid container spacing={2} alignItems="flex-end">
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <TextField
                            label="Your Price Offer (CAD)"
                            type="number"
                            fullWidth
                            value={requestBid.price}
                            onChange={(e) => {
                              setRequestBids(prev => ({
                                ...prev,
                                [request.id]: { ...requestBid, price: e.target.value, error: '' }
                              }));
                            }}
                            disabled={requestBid.submitting}
                            sx={{ fontSize: '1.5em' }}
                            InputProps={{
                              startAdornment: <Typography sx={{ mr: 1, fontSize: '1.5em' }}>$</Typography>,
                            }}
                            helperText={
                              request.minBudget && request.maxBudget
                                ? `Must be between $${request.minBudget} - $${request.maxBudget}`
                                : request.maxBudget
                                  ? `Must be less than $${request.maxBudget}`
                                  : ''
                            }
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            label="Message (Optional)"
                            multiline
                            rows={2}
                            fullWidth
                            value={requestBid.message}
                            onChange={(e) => {
                              setRequestBids(prev => ({
                                ...prev,
                                [request.id]: { ...requestBid, message: e.target.value, error: '' }
                              }));
                            }}
                            disabled={requestBid.submitting}
                            placeholder="Add a message to the passenger..."
                            sx={{ fontSize: '1.5em' }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 2 }}>
                          <Button
                            variant="contained"
                            color="secondary"
                            fullWidth
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSubmitBidInline(request);
                            }}
                            disabled={requestBid.submitting || !requestBid.price}
                            sx={{ fontSize: '1.5em', py: 1.5, height: '56px' }}
                          >
                            {requestBid.submitting ? 'Sending...' : 'Bid'}
                          </Button>
                        </Grid>
                      </Grid>
                    </Box>
                  ) : null
                ) : user ? (
                  <Alert severity="info" sx={{ fontSize: '1.5em', '& .MuiAlert-message': { fontSize: '1.5em' } }}>
                    <Typography variant="body2" sx={{ mb: 1, fontSize: '1.5em' }}>
                      You need to become a driver to place bids on ride requests.
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleBecomeDriverRedirect();
                      }}
                      sx={{ fontSize: '1.5em' }}
                    >
                      Become a Driver
                    </Button>
                  </Alert>
                ) : (
                  <Alert severity="info" sx={{ fontSize: '1.5em', '& .MuiAlert-message': { fontSize: '1.5em' } }}>
                    <Typography variant="body2" sx={{ mb: 1, fontSize: '1.5em' }}>
                      Please log in and become a driver to place bids on ride requests.
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        const returnUrl = location.pathname + location.search;
                        navigate('/login', { state: { returnUrl } });
                      }}
                      sx={{ fontSize: '1.5em' }}
                    >
                      Log In
                    </Button>
                  </Alert>
                )}
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>
    );
  };

  return (
    <AuthBackground maxWidth="70%">
      <Box sx={{ width: '100%', position: 'relative', zIndex: 1, p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 2, fontSize: '4.78125rem' }}>
            🔍 Find Rides
          </Typography>

          {originCity && destinationCity && (
            <Paper sx={{ p: 2, mb: 3, bgcolor: 'primary.light', color: 'white' }}>
              <Typography variant="h6" sx={{ fontSize: '2.8125rem' }}>
                {originCity.name} → {destinationCity.name}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '1.96875rem' }}>
                {anyDate ? 'Any date' : (departureDate ? new Date(departureDate).toLocaleDateString() : 'Any date')} • {passengers} passenger{passengers > 1 ? 's' : ''}
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={anyDate}
                    onChange={(e) => {
                      setAnyDate(e.target.checked);
                      if (e.target.checked) {
                        searchRides();
                      }
                    }}
                    sx={{
                      '& .MuiSvgIcon-root': { fontSize: 36 }
                    }}
                  />
                }
                label="Find rides on any date"
                sx={{
                  mt: 1,
                  '& .MuiTypography-root': {
                    fontSize: '1.6875rem'
                  }
                }}
              />
            </Paper>
          )}

          {noResults && (
            <Alert severity="info" sx={{ mb: 3, fontSize: '1.96875rem', '& .MuiAlert-message': { fontSize: '1.96875rem' } }}>
              No rides found for your search criteria. Showing all available rides instead.
              <Button
                variant="text"
                onClick={() => navigate('/request-ride', {
                  state: { originCity, destinationCity, departureDate, passengers }
                })}
                sx={{ ml: 2, fontSize: '1.96875rem' }}
              >
                Request a Ride
              </Button>
            </Alert>
          )}
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3, fontSize: '1.96875rem', '& .MuiAlert-message': { fontSize: '1.96875rem' } }}>
            {error}
          </Alert>
        )}

        {/* Loading */}
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* View Toggle and Results */}
        {!isLoading && results.length > 0 && (
          <Box>
            {/* Header with view toggle */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ fontSize: '2.8125rem' }}>
                {viewMode === 'list'
                  ? `${results.length} ${searchType === 'requested' ? 'request' : 'ride'}${results.length > 1 ? 's' : ''} found`
                  : selectedDate
                    ? `${filteredResults.length} ${searchType === 'requested' ? 'request' : 'ride'}${filteredResults.length !== 1 ? 's' : ''} on ${selectedDate.toLocaleDateString()}`
                    : 'Select a date to view results'
                }
              </Typography>

              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(e, newMode) => {
                  if (newMode !== null) {
                    setViewMode(newMode);
                    setSelectedDate(null);
                  }
                }}
                size="small"
              >
                <ToggleButton value="list" sx={{ fontSize: '1.96875rem' }}>
                  <ListIcon sx={{ mr: 1 }} />
                  List
                </ToggleButton>
                <ToggleButton value="calendar" sx={{ fontSize: '1.96875rem' }}>
                  <CalendarIcon sx={{ mr: 1 }} />
                  Calendar
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Calendar View */}
            {viewMode === 'calendar' && (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
                  <Box sx={{ flex: '0 0 auto', width: { xs: '100%', md: '400px' } }}>
                    <Paper sx={{ p: 2 }}>
                      <DateCalendar
                        value={selectedDate}
                        onChange={(date) => setSelectedDate(date)}
                        slots={{
                          day: CustomDay,
                        }}
                        disablePast
                      />
                    </Paper>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    {selectedDate ? (
                      filteredResults.length > 0 ? (
                        <Box>
                          {filteredResults.map(renderResultCard)}
                        </Box>
                      ) : (
                        <Paper sx={{ p: 4, textAlign: 'center' }}>
                          <Typography variant="body1" color="text.secondary">
                            No rides available on this date
                          </Typography>
                        </Paper>
                      )
                    ) : (
                      <Paper sx={{ p: 4, textAlign: 'center' }}>
                        <CalendarIcon sx={{ fontSize: 90, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" sx={{ fontSize: '2.8125rem' }}>
                          Select a date from the calendar
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontSize: '1.96875rem' }}>
                          Dates with rides are highlighted
                        </Typography>
                      </Paper>
                    )}
                  </Box>
                </Box>
              </LocalizationProvider>
            )}

            {/* List View */}
            {viewMode === 'list' && (
              <Box>
                {results.map(renderResultCard)}
              </Box>
            )}
          </Box>
        )}

        {/* Empty State */}
        {!isLoading && results.length === 0 && (
          <Paper
            sx={{
              p: 6,
              textAlign: 'center',
              borderRadius: 3,
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'center', gap: { xs: 4, sm: 8 } }}>
              <Box>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 2, fontSize: '2.8125rem' }}>
                  {t('searchResults.emptyState.beFirstDriver')}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => navigate('/create-ride')}
                  sx={{ fontSize: '3.375rem', py: 1.5, minWidth: '300px' }}
                >
                  {t('searchResults.emptyState.offerRide')}
                </Button>
              </Box>

              <Box>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 2, fontSize: '2.8125rem' }}>
                  {t('searchResults.emptyState.postRequest')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => navigate('/request-ride')}
                    sx={{ fontSize: '3.375rem', py: 1.5, minWidth: '300px' }}
                  >
                    {t('searchResults.emptyState.requestRide')}
                  </Button>
                </Box>
              </Box>
            </Box>
          </Paper>
        )}

        {/* Ride Details Modal (Modern) */}
        {selectedItem && isRide(selectedItem) && (
          <RideDetailsModal
            isOpen={detailDialogOpen}
            onClose={() => {
              setDetailDialogOpen(false);
              setSelectedItem(null);
            }}
            ride={selectedItem}
            onJoin={(rideId: string, seats: number) => {
              setDetailDialogOpen(false);
              navigate(`/ride/${rideId}`, {
                state: {
                  ride: selectedItem,
                  showBooking: true,
                  initialSeats: seats
                }
              });
            }}
          />
        )}

        {/* Existing Details Dialog (For Ride Requests Only) */}
        <Dialog
          open={detailDialogOpen && !!selectedItem && !isRide(selectedItem)}
          onClose={() => {
            setDetailDialogOpen(false);
            setSelectedItem(null);
            setBidPrice('');
            setBidMessage('');
            setError('');
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ fontSize: '2.8125rem' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontSize: '2.8125rem' }}>
                {selectedItem && isRideRequest(selectedItem) ? 'Ride Request Details' : 'Details'}
              </Typography>
              <IconButton onClick={() => {
                setDetailDialogOpen(false);
                setSelectedItem(null);
                setBidPrice('');
                setBidMessage('');
                setError('');
              }}>
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            {selectedItem && isRideRequest(selectedItem) && renderRequestDetails(selectedItem)}
          </DialogContent>
        </Dialog>
      </Box>
    </AuthBackground>
  );
};

export default FindRidesPage;